import type { Entitlement } from '@dash-lite/sdk';
import { CreditCard, KeyRound } from 'lucide-react';
import { Badge, Card, CardBody, CardHeader, DataList, DataRow } from '@/components/ui/primitives';
import { formatDateTime, formatMoney } from '@/lib/format';

/**
 * Récapitulatif d'un droit d'accès — `GET /entitlement`.
 *
 * Une seule réponse suffit à construire un écran « mon compte » complet :
 *
 * - `has_access` — **le seul champ à consulter pour ouvrir l'accès**. Il tient
 *   déjà compte du délai de grâce après un échec de paiement et de la fin de
 *   période payée après une résiliation. Ne le recalculez jamais à partir des
 *   dates : vous obtiendriez un résultat différent de celui du serveur.
 * - `status` — `none`, `active`, `past_due`, `canceled` ou `expired`, pour
 *   informer le visiteur de sa situation.
 * - `ends_at` — fin de l'accès. `renews_at` — prochain prélèvement d'un
 *   abonnement en cours, `null` s'il est résilié. Les deux ne se confondent
 *   pas : après une résiliation, `ends_at` est renseigné et `renews_at` ne
 *   l'est plus.
 * - `offer` — ce qui a été acheté, avec son tarif.
 * - `card` — de quoi afficher « Visa •••• 4242 ».
 * - `metadata` — le contexte que vous aviez attaché à l'achat.
 */
export function EntitlementSummary({ entitlement }: { entitlement: Entitlement }) {
    return (
        <Card>
            <CardHeader
                title="Votre accès"
                icon={<KeyRound className="size-4" />}
                action={<EntitlementStatusBadge entitlement={entitlement} />}
            />

            <CardBody>
                <DataList>
                    {entitlement.offer && (
                        <>
                            <DataRow label="Offre" value={entitlement.offer.name} />
                            <DataRow label="Tarif" value={formatMoney(entitlement.offer.price)} />
                        </>
                    )}

                    <DataRow label="Ouvert le" value={formatDateTime(entitlement.starts_at)} />

                    <DataRow
                        label="Accès jusqu'au"
                        value={
                            entitlement.ends_at ? (
                                formatDateTime(entitlement.ends_at)
                            ) : (
                                <span className="text-ink-muted">
                                    Sans échéance tant que l'abonnement est actif
                                </span>
                            )
                        }
                    />

                    <DataRow
                        label="Prochain prélèvement"
                        value={
                            entitlement.renews_at ? (
                                formatDateTime(entitlement.renews_at)
                            ) : (
                                <span className="text-ink-muted">Aucun — rien ne sera prélevé</span>
                            )
                        }
                    />

                    {entitlement.card && (
                        <DataRow
                            label="Moyen de paiement"
                            value={
                                <span className="inline-flex items-center gap-2">
                                    <CreditCard className="text-ink-muted size-4" aria-hidden />
                                    <span className="capitalize">{entitlement.card.brand ?? 'Carte'}</span>
                                    <span className="font-mono">•••• {entitlement.card.last4}</span>
                                </span>
                            }
                        />
                    )}
                </DataList>

                {entitlement.metadata && Object.keys(entitlement.metadata).length > 0 && (
                    <div className="border-line mt-5 border-t pt-4">
                       
                    </div>
                )}
            </CardBody>
        </Card>
    );
}

/**
 * Badge d'état.
 *
 * Noter le cas `canceled` : l'accès reste **ouvert** jusqu'à `ends_at`. Le
 * client garde ce qu'il a déjà payé — c'est normal, et c'est précisément ce
 * que l'interface doit annoncer plutôt que de laisser croire à une coupure
 * immédiate.
 */
export function EntitlementStatusBadge({ entitlement }: { entitlement: Entitlement }) {
    switch (entitlement.status) {
        case 'active':
            return <Badge tone="positive">Accès actif</Badge>;

        case 'past_due':
            return <Badge tone="caution">Paiement en attente</Badge>;

        case 'canceled':
            return (
                <Badge tone={entitlement.has_access ? 'caution' : 'neutral'}>
                    {entitlement.has_access ? 'Résilié — accès jusqu’à échéance' : 'Résilié'}
                </Badge>
            );

        case 'expired':
            return <Badge tone="neutral">Accès expiré</Badge>;

        default:
            return <Badge tone="neutral">Aucun accès</Badge>;
    }
}
